import { useEffect } from "react";
import { useParams, useLocation, Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { CalendarDays, Clock, Eye, ArrowLeft, Share2, Tag } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { sanitizeHtml } from "@/lib/sanitize";
import Navbar from "@/components/navbar";
import Footer from "@/components/footer";

interface BlogPost {
  id: number;
  title: string;
  slug: string;
  content: string;
  metaTitle: string;
  metaDescription: string;
  keywords: string;
  featuredImage: string;
  imageAlt: string;
  category: string;
  tags: string[];
  readTime: number;
  viewCount: number;
  publishedAt: string;
  author?: {
    firstName: string;
    lastName: string;
  };
}

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const [, navigate] = useLocation();

  const { data: post, isLoading, error } = useQuery<BlogPost>({
    queryKey: ['/api/blog', slug],
    queryFn: async () => {
      const response = await fetch(`/api/blog/${slug}`);
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Blog post not found');
        }
        throw new Error('Failed to fetch blog post');
      }
      return response.json();
    },
    enabled: !!slug,
  });

  // Set dynamic SEO meta tags
  useEffect(() => {
    if (post) {
      // Set page title
      document.title = post.metaTitle || `${post.title} | Prize Drop Blog`;
      
      // Set meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', post.metaDescription || post.content.substring(0, 160) + '...');
      } else {
        const meta = document.createElement('meta');
        meta.name = 'description';
        meta.content = post.metaDescription || post.content.substring(0, 160) + '...';
        document.head.appendChild(meta);
      }

      // Set keywords meta tag
      if (post.keywords) {
        let keywordsMeta = document.querySelector('meta[name="keywords"]');
        if (keywordsMeta) {
          keywordsMeta.setAttribute('content', post.keywords);
        } else {
          keywordsMeta = document.createElement('meta');
          keywordsMeta.setAttribute('name', 'keywords');
          keywordsMeta.setAttribute('content', post.keywords);
          document.head.appendChild(keywordsMeta);
        }
      }

      // Open Graph meta tags
      const setOpenGraphTag = (property: string, content: string) => {
        let tag = document.querySelector(`meta[property="${property}"]`);
        if (tag) {
          tag.setAttribute('content', content);
        } else {
          tag = document.createElement('meta');
          tag.setAttribute('property', property);
          tag.setAttribute('content', content);
          document.head.appendChild(tag);
        }
      };

      setOpenGraphTag('og:title', post.title);
      setOpenGraphTag('og:description', post.metaDescription || post.content.substring(0, 160) + '...');
      setOpenGraphTag('og:type', 'article');
      setOpenGraphTag('og:url', window.location.href);
      if (post.featuredImage) {
        setOpenGraphTag('og:image', post.featuredImage);
      }

      // Twitter Card meta tags
      const setTwitterTag = (name: string, content: string) => {
        let tag = document.querySelector(`meta[name="${name}"]`);
        if (tag) {
          tag.setAttribute('content', content);
        } else {
          tag = document.createElement('meta');
          tag.setAttribute('name', name);
          tag.setAttribute('content', content);
          document.head.appendChild(tag);
        }
      };

      setTwitterTag('twitter:card', 'summary_large_image');
      setTwitterTag('twitter:title', post.title);
      setTwitterTag('twitter:description', post.metaDescription || post.content.substring(0, 160) + '...');
      if (post.featuredImage) {
        setTwitterTag('twitter:image', post.featuredImage);
      }

      // Structured data for blog post
      const structuredData = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": post.title,
        "description": post.metaDescription || post.content.substring(0, 160) + '...',
        "image": post.featuredImage,
        "author": {
          "@type": "Person",
          "name": post.author ? `${post.author.firstName} ${post.author.lastName}` : "Prize Drop Team"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Prize Drop",
          "logo": {
            "@type": "ImageObject",
            "url": "/logo.png"
          }
        },
        "datePublished": post.publishedAt,
        "dateModified": post.publishedAt,
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": window.location.href
        },
        "keywords": post.keywords,
        "articleSection": post.category,
        "wordCount": post.content.split(' ').length,
        "timeRequired": `PT${post.readTime || 5}M`,
        "interactionStatistic": {
          "@type": "InteractionCounter",
          "interactionType": "https://schema.org/ReadAction",
          "userInteractionCount": post.viewCount || 0
        }
      };

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);

      return () => {
        // Cleanup structured data on unmount
        document.querySelectorAll('script[type="application/ld+json"]').forEach(script => {
          if (script.textContent?.includes(post.title)) {
            script.remove();
          }
        });
      };
    }
  }, [post]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const sharePost = async () => {
    if (navigator.share && post) {
      try {
        await navigator.share({
          title: post.title,
          text: post.metaDescription || post.content.substring(0, 160) + '...',
          url: window.location.href,
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      // Fallback to copying URL to clipboard
      navigator.clipboard.writeText(window.location.href);
      // You could show a toast notification here
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-24 mb-6"></div>
            <div className="h-12 bg-gray-200 rounded w-3/4 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
            <div className="h-64 bg-gray-200 rounded mb-8"></div>
            <div className="space-y-4">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="h-4 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Blog Post Not Found</h1>
          <p className="text-gray-600 mb-8">
            The blog post you're looking for doesn't exist or has been removed.
          </p>
          <Link href="/blog">
            <Button data-testid="back-to-blog">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!post) return null;

  return (
    <>
      <Navbar />
      <article className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Back to blog link */}
        <Link href="/blog">
          <Button variant="ghost" className="mb-6" data-testid="back-to-blog-top">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </Button>
        </Link>

        <Card className="overflow-hidden">
          {/* Featured image */}
          {post.featuredImage && (
            <div className="aspect-video overflow-hidden">
              <img
                src={post.featuredImage}
                alt={post.imageAlt || post.title}
                className="w-full h-full object-cover"
                data-testid="blog-post-featured-image"
                onError={(e) => {
                  console.error('Featured image failed to load:', post.featuredImage);
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          )}

          <CardContent className="p-8">
            {/* Header */}
            <header className="mb-8">
              {post.category && (
                <Badge variant="secondary" className="mb-4" data-testid="blog-post-category">
                  {post.category}
                </Badge>
              )}
              
              <h1 className="text-4xl font-bold text-gray-900 leading-tight mb-4" data-testid="blog-post-title">
                {post.title}
              </h1>

              {/* Meta information */}
              <div className="flex flex-wrap items-center justify-between gap-4 text-sm text-gray-600 mb-6">
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-1">
                    <CalendarDays className="h-4 w-4" />
                    <span data-testid="blog-post-date">{formatDate(post.publishedAt)}</span>
                  </div>
                  
                  <div className="flex items-center space-x-1">
                    <span>By {post.author ? `${post.author.firstName} ${post.author.lastName}` : "Prize Drop Team"}</span>
                  </div>
                  
                  {post.readTime && (
                    <div className="flex items-center space-x-1">
                      <Clock className="h-4 w-4" />
                      <span data-testid="blog-post-read-time">{post.readTime} min read</span>
                    </div>
                  )}
                  
                  <div className="flex items-center space-x-1">
                    <Eye className="h-4 w-4" />
                    <span data-testid="blog-post-views">{post.viewCount || 0} views</span>
                  </div>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={sharePost}
                  data-testid="blog-post-share"
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Share
                </Button>
              </div>

              <Separator />
            </header>

            {/* Content */}
            <div className="prose prose-lg max-w-none mb-8" data-testid="blog-post-content">
              <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(post.content) }} />
            </div>

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <>
                <Separator className="mb-6" />
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Tag className="h-4 w-4" />
                    <span>Tags:</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map((tag, index) => (
                      <Badge key={index} variant="outline" data-testid={`blog-post-tag-${index}`}>
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Related posts / Call to action */}
        <div className="mt-12 text-center">
          <Card className="p-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Ready to Start Winning?
            </h2>
            <p className="text-gray-600 mb-6">
              Put these tips into action and start entering competitions on Prize Drop today!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/competitions">
                <Button size="lg" data-testid="view-competitions-cta">
                  View Active Competitions
                </Button>
              </Link>
              <Link href="/blog">
                <Button variant="outline" size="lg" data-testid="more-tips-cta">
                  Read More Tips
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </div>
      <Footer />
    </article>
    </>
  );
}